/**
 * @file Строка файла или папки и её локальные режимы редактирования.
 *
 * view показывает элемент, rename — форму имени, delete — подтверждение.
 * HTTP-операции приходят через props: форма ждёт их результата и сохраняет
 * введённое значение при ошибке.
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { FormEvent, ReactNode, RefObject } from 'react'
import { FiDownload, FiFile, FiFolder, FiMoreHorizontal } from 'react-icons/fi'

import { ContextMenu, Menu } from '@base-ui/react'

import { fileMessages } from '../features/files/messages'
import { fileErrorCode, validateRenameName } from '../features/files/model'
import type { FileErrorCode } from '../features/files/model'
import { useLocale } from '../i18n/useLocale'
import FileContextMenu from './FileContextMenu'

type Props = {
    name: string
    isFolder: boolean
    taskInProgress: boolean
    disabled: boolean
    activity: string | null
    existingNames: ReadonlySet<string>
    onOpen: () => void
    onDownload: () => void
    onDelete: () => Promise<void>
    onRename: (name: string) => Promise<void>
}

export default function FileRow({
    name,
    isFolder,
    taskInProgress,
    disabled,
    activity,
    existingNames,
    onOpen,
    onDownload,
    onDelete,
    onRename,
}: Props) {
    const { locale } = useLocale()
    const text = fileMessages[locale]

    // Режим строки и введённые данные должны переживать перерисовку.
    const id = useId()
    const [mode, setMode] = useState<'view' | 'rename' | 'delete'>('view')
    const [newName, setNewName] = useState(name)
    const [error, setError] = useState<FileErrorCode | null>(null)
    const [pending, setPending] = useState(false)

    // pending меняет экран; lock запрещает двойную отправку в одном цикле событий.
    const lock = useRef(false)

    // Эти ссылки отвечают только за возврат клавиатурного фокуса.
    const restoreFocus = useRef(false)
    const focusTarget = useRef<HTMLButtonElement>(null)
    const input = useRef<HTMLInputElement>(null)
    const cancelButton = useRef<HTMLButtonElement>(null)

    // Поздний ответ запроса не должен менять уже удалённую строку.
    const mounted = useRef(true)

    useEffect(() => {
        mounted.current = true

        return () => {
            mounted.current = false
        }
    }, [])

    useEffect(() => {
        if (mode === 'rename') {
            input.current?.focus()
            // У файла выделяем имя без расширения; у папки — название целиком.
            const dot = name.lastIndexOf('.')
            input.current?.setSelectionRange(
                0,
                !isFolder && dot > 0 ? dot : name.length,
            )
        } else if (mode === 'delete') cancelButton.current?.focus()
    }, [mode, name, isFolder])

    const unavailable = disabled || taskInProgress || pending

    useEffect(() => {
        if (mode === 'view' && restoreFocus.current && !unavailable) {
            focusTarget.current?.focus()
            restoreFocus.current = false
        }
    }, [mode, unavailable])

    function begin(next: 'rename' | 'delete') {
        if (unavailable) return

        setNewName(name)
        setError(null)
        setMode(next)
    }

    function cancel() {
        if (lock.current) return

        setMode('view')
        setError(null)
        restoreFocus.current = true
    }

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        if (lock.current || disabled || taskInProgress) return

        if (mode === 'rename') {
            const validation = validateRenameName(newName, name, existingNames)
            setError(validation)
            if (validation) {
                input.current?.focus()

                return
            }

            if (newName.trim() === name) {
                cancel()

                return
            }
        }

        // Режим формы закрываем только после подтверждения операции.
        lock.current = true
        setPending(true)
        setError(null)

        try {
            if (mode === 'rename') await onRename(newName.trim())
            else await onDelete()
            if (mounted.current) {
                if (mode === 'delete')
                    cancelButton.current
                        ?.closest<HTMLElement>('.file-list')
                        ?.focus()

                setMode('view')
                restoreFocus.current = mode === 'rename'
            }
        } catch (cause) {
            if (mounted.current) setError(fileErrorCode(cause))
        } finally {
            lock.current = false
            if (mounted.current) setPending(false)
        }
    }

    const actions = (
        <FileContextMenu
            isFolder={isFolder}
            onOpen={onOpen}
            onDownload={onDownload}
            onRename={() => begin('rename')}
            onDelete={() => begin('delete')}
        />
    )

    return (
        <ContextMenu.Root disabled={mode !== 'view' || unavailable}>
            {/* Контекстное меню отключено во время редактирования или занятой операции. */}
            <ContextMenu.Trigger
                render={<article />}
                className={`file-row${mode !== 'view' ? ' file-row-editing' : ''}`}
                aria-label={`${isFolder ? text.folder : text.file}: ${name}`}
                aria-busy={pending || taskInProgress}
            >
                <span
                    className={`file-row-icon${isFolder ? ' file-row-folder' : ''}`}
                    aria-hidden='true'
                >
                    {isFolder ? <FiFolder /> : <FiFile />}
                </span>

                {/* В режиме просмотра доступны открытие папки и действия; иначе строку заменяет форма. */}
                {mode === 'view' ? (
                    <FileRowView
                        name={name}
                        isFolder={isFolder}
                        activity={activity}
                        unavailable={unavailable}
                        onOpen={onOpen}
                        onDownload={onDownload}
                        focusTarget={focusTarget}
                        finalFocus={() =>
                            mode === 'view' ? focusTarget.current : false
                        }
                        actions={actions}
                    />
                ) : (
                    <FileRowEditor
                        mode={mode}
                        name={name}
                        isFolder={isFolder}
                        id={id}
                        newName={newName}
                        error={error}
                        pending={pending}
                        unavailable={unavailable}
                        input={input}
                        cancelButton={cancelButton}
                        onSubmit={submit}
                        onCancel={cancel}
                        onNameChange={(value) => {
                            setNewName(value)
                            setError(null)
                        }}
                    />
                )}
            </ContextMenu.Trigger>

            {/* Popup выводится через portal вне строки, чтобы список не обрезал выпадающее меню. */}
            <ContextMenu.Portal>
                <ContextMenu.Positioner className='file-menu-positioner'>
                    <ContextMenu.Popup
                        className='file-menu'
                        finalFocus={() =>
                            mode === 'view' ? focusTarget.current : false
                        }
                    >
                        {actions}
                    </ContextMenu.Popup>
                </ContextMenu.Positioner>
            </ContextMenu.Portal>
        </ContextMenu.Root>
    )
}

// Представления ниже получают готовое состояние и действия. Владельцем режима,
// блокировки и фокуса остаётся FileRow: HTTP-запросы не переезжают в разметку.
function FileRowView({
    name,
    isFolder,
    activity,
    unavailable,
    onOpen,
    onDownload,
    focusTarget,
    finalFocus,
    actions,
}: {
    name: string
    isFolder: boolean
    activity: string | null
    unavailable: boolean
    onOpen: () => void
    onDownload: () => void
    focusTarget: RefObject<HTMLButtonElement | null>
    finalFocus: () => HTMLButtonElement | null | false
    actions: ReactNode
}) {
    const { locale } = useLocale()
    const text = fileMessages[locale]

    return (
        <>
            {isFolder ? (
                <button
                    type='button'
                    className='file-row-info file-folder-open'
                    onClick={onOpen}
                    disabled={unavailable}
                    title={name}
                >
                    <span className='file-row-name'>{name}</span>
                    <span
                        className='file-row-type'
                        role={activity ? 'status' : undefined}
                    >
                        {activity || text.folder}
                    </span>
                </button>
            ) : (
                <div className='file-row-info'>
                    <span className='file-row-name' title={name}>
                        {name}
                    </span>
                    <span
                        className='file-row-type'
                        role={activity ? 'status' : undefined}
                    >
                        {activity || text.file}
                    </span>
                </div>
            )}

            {/* Быстрое скачивание вынесено отдельно, остальные действия доступны через меню. */}
            <div className='file-row-actions'>
                <button
                    type='button'
                    className='file-icon-button'
                    onClick={onDownload}
                    disabled={unavailable}
                    title={isFolder ? text.downloadFolder : text.download}
                    aria-label={`${isFolder ? text.downloadFolder : text.download}: ${name}`}
                >
                    <FiDownload aria-hidden='true' />
                </button>

                <Menu.Root disabled={unavailable}>
                    <Menu.Trigger
                        ref={focusTarget}
                        className='file-icon-button'
                        title={text.actions}
                        aria-label={`${text.actions}: ${name}`}
                    >
                        <FiMoreHorizontal aria-hidden='true' />
                    </Menu.Trigger>
                    <Menu.Portal>
                        <Menu.Positioner
                            sideOffset={6}
                            align='end'
                            className='file-menu-positioner'
                        >
                            <Menu.Popup
                                className='file-menu'
                                finalFocus={finalFocus}
                            >
                                {actions}
                            </Menu.Popup>
                        </Menu.Positioner>
                    </Menu.Portal>
                </Menu.Root>
            </div>
        </>
    )
}

// Одна форма обслуживает переименование и подтверждение удаления.
// Обработчик submit родителя проверяет имя и ждёт подтверждения сервера.
function FileRowEditor({
    mode,
    name,
    isFolder,
    id,
    newName,
    error,
    pending,
    unavailable,
    input,
    cancelButton,
    onSubmit,
    onCancel,
    onNameChange,
}: {
    mode: 'rename' | 'delete'
    name: string
    isFolder: boolean
    id: string
    newName: string
    error: FileErrorCode | null
    pending: boolean
    unavailable: boolean
    input: RefObject<HTMLInputElement | null>
    cancelButton: RefObject<HTMLButtonElement | null>
    onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>
    onCancel: () => void
    onNameChange: (value: string) => void
}) {
    const { locale } = useLocale()
    const text = fileMessages[locale]
    const submitLabel = pending
        ? mode === 'delete'
            ? text.deleting
            : text.renaming
        : mode === 'delete'
          ? text.confirmDelete
          : text.save

    return (
        <form
            className='file-row-editor'
            aria-label={`${mode === 'rename' ? text.rename : text.remove}: ${name}`}
            onSubmit={onSubmit}
            noValidate
            onKeyDown={(event) => {
                if (event.nativeEvent.isComposing) {
                    if (event.key === 'Enter') event.preventDefault()

                    return
                }

                if (event.key === 'Escape' && !pending) {
                    event.preventDefault()
                    onCancel()
                }
            }}
        >
            {/* Один обработчик submit обслуживает два режима, но удаление требует явного подтверждения. */}
            {mode === 'rename' ? (
                <div className='file-rename-field'>
                    <label className='file-sr-only' htmlFor={`${id}-name`}>
                        {text.name}
                    </label>
                    <input
                        ref={input}
                        id={`${id}-name`}
                        autoComplete='off'
                        spellCheck={false}
                        value={newName}
                        disabled={unavailable}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? `${id}-error` : undefined}
                        onChange={(event) => {
                            onNameChange(event.target.value)
                        }}
                    />
                </div>
            ) : (
                <div className='file-delete-message'>
                    <strong>
                        {isFolder ? text.confirmFolder : text.confirmFile}
                    </strong>
                    <span className='file-delete-name'>{name}</span>
                    <span>{text.permanent}</span>
                </div>
            )}

            {/* Отмена возвращает режим просмотра; во время запроса повторная отправка заблокирована. */}
            <div className='file-editor-actions'>
                <button
                    type='button'
                    ref={cancelButton}
                    disabled={pending}
                    onClick={onCancel}
                >
                    {text.cancel}
                </button>

                <button
                    type='submit'
                    className={
                        mode === 'delete'
                            ? 'file-delete-confirm'
                            : 'file-rename-save'
                    }
                    disabled={unavailable}
                >
                    {submitLabel}
                </button>
            </div>

            {error && (
                <p className='file-row-error' id={`${id}-error`} role='alert'>
                    {text.errors[error]}
                </p>
            )}
        </form>
    )
}
